// Run the LLM classifier — `POST /api/ontology/tags/classify` starts a
// `kind: 'tag_classification'` ingest job that runs server-side, 100 tags
// per LLM call. The endpoint returns `202 { jobId, total }` immediately;
// `runTagClassification` re-reads the job row on each `jobs` wake-up until it
// reaches a terminal state, surfacing the running counters from the job
// `payload` as progress. Per-tag results and errors land in the job's logs,
// viewable on the Ingest page (debug mode).

import { getOntologyServiceBaseUrl } from '../../../config'
import { nextDomainTopic } from '../../cache-coherence/domain-event-bus'
import { createOntologyHeaders } from '../../core'
import type { TagRecord } from '../tags-api'
import type { TagClassificationJobState, TagClassificationProgress } from './types'
import { postJson } from './wire'

// Re-read interval when no wake-up arrives, e.g. while the events stream is down.
const JOB_WAKE_FALLBACK_MS = 30_000
// Transient /jobs/:id fetch failures tolerated before the poll gives up.
const JOB_POLL_MAX_CONSECUTIVE_FAILURES = 4

export interface RunTagClassificationArgs {
  tags: Array<
    Pick<TagRecord, 'tagPath' | 'sourceUrl' | 'sourceType' | 'displayLabel'> & { nodeId: string }
  >
  /** Display scope for the job row on the Ingest page (e.g. folder path). */
  scope?: string
  onProgress?: (progress: TagClassificationProgress) => void
  signal?: AbortSignal
}

export interface RunTagClassificationResult {
  jobId: string
  state: TagClassificationJobState
  total: number
  classifiedCount: number
  skippedCount: number
  errorCount: number
  branchId?: string
}

/** Start a classification job without waiting for it. */
export async function startTagClassification(
  tags: RunTagClassificationArgs['tags'],
  scope?: string,
  signal?: AbortSignal
): Promise<{ jobId: string; total: number }> {
  return postJson<{ jobId: string; total: number }>(
    '/tags/classify',
    {
      scope,
      tags: tags.map((t) => ({
        nodeId: t.nodeId,
        tagPath: t.tagPath,
        sourceUrl: t.sourceUrl,
        sourceType: t.sourceType,
        displayLabel: t.displayLabel || undefined
      }))
    },
    signal
  )
}

interface JobSnapshot {
  state: TagClassificationJobState
  progress: TagClassificationProgress
  branchId?: string
}

function jobSnapshotFromJson(raw: unknown, total: number): JobSnapshot {
  const j = raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const payload =
    j.payload !== null && typeof j.payload === 'object'
      ? (j.payload as Record<string, unknown>)
      : {}
  const count = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0)
  return {
    state: typeof j.state === 'string' ? (j.state as TagClassificationJobState) : 'processing',
    progress: {
      done: count(payload.processed),
      total: count(payload.total) || total,
      classifiedCount: count(payload.classified),
      skippedCount: count(payload.skipped),
      errorCount: count(payload.errors)
    },
    branchId: typeof j.branchId === 'string' && j.branchId ? j.branchId : undefined
  }
}

async function fetchJobSnapshot(
  jobId: string,
  total: number,
  signal?: AbortSignal
): Promise<JobSnapshot> {
  const resp = await fetch(`${getOntologyServiceBaseUrl()}/jobs/${encodeURIComponent(jobId)}`, {
    headers: createOntologyHeaders(),
    signal
  })
  if (!resp.ok) throw new Error(`${resp.status} ${resp.statusText}`)
  return jobSnapshotFromJson(await resp.json(), total)
}

const TERMINAL_STATES: ReadonlySet<TagClassificationJobState> = new Set([
  'done',
  'error',
  'cancelled'
])

/**
 * Start a server-side classification job over the given tags and follow it to
 * completion. Aborting the signal stops following only — the job keeps
 * running server-side and stays visible on the Ingest page.
 */
export async function runTagClassification({
  tags,
  scope,
  onProgress,
  signal
}: RunTagClassificationArgs): Promise<RunTagClassificationResult> {
  const waits = new AbortController()
  const stopWaits = (): void => waits.abort()
  signal?.addEventListener('abort', stopWaits, { once: true })
  // Armed before each request so a wake-up that lands during it isn't missed.
  let wake = nextDomainTopic('jobs', JOB_WAKE_FALLBACK_MS, waits.signal)
  try {
    const { jobId, total } = await startTagClassification(tags, scope, signal)

    let snapshot: JobSnapshot = {
      state: 'processing',
      progress: { done: 0, total, classifiedCount: 0, skippedCount: 0, errorCount: 0 }
    }
    let consecutiveFailures = 0
    while (!signal?.aborted) {
      await wake
      if (signal?.aborted) break
      wake = nextDomainTopic('jobs', JOB_WAKE_FALLBACK_MS, waits.signal)
      try {
        snapshot = await fetchJobSnapshot(jobId, total, signal)
        consecutiveFailures = 0
      } catch (err) {
        if (signal?.aborted) break
        consecutiveFailures += 1
        if (consecutiveFailures >= JOB_POLL_MAX_CONSECUTIVE_FAILURES) {
          throw new Error(
            `lost track of classification job ${jobId}: ${err instanceof Error ? err.message : String(err)}`
          )
        }
        continue
      }
      onProgress?.(snapshot.progress)
      if (TERMINAL_STATES.has(snapshot.state)) break
    }

    return {
      jobId,
      state: snapshot.state,
      total: snapshot.progress.total,
      classifiedCount: snapshot.progress.classifiedCount,
      skippedCount: snapshot.progress.skippedCount,
      errorCount: snapshot.progress.errorCount,
      branchId: snapshot.branchId
    }
  } finally {
    signal?.removeEventListener('abort', stopWaits)
    waits.abort()
  }
}
